import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Circle, MapPin, X } from 'lucide-react';
import type { FieldEngineer, FieldJob, FieldJobStatus } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatChecklistValue } from '../../lib/field-checklist';
import { formatRelative } from '../../lib/format';
import { Button, ErrorNotice, Field, Pill, type PillTone } from '../../components/ui';

const STATUS_TONE: Record<FieldJobStatus, PillTone> = {
  assigned: 'info',
  in_progress: 'primary',
  complete: 'success',
  incomplete: 'warn',
  closed: 'neutral',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<FieldJobStatus, string> = {
  assigned: 'Assigned',
  in_progress: 'In progress',
  complete: 'Complete',
  incomplete: 'Incomplete',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const INCOMPLETE_LABEL: Record<string, string> = {
  gps_accuracy: 'GPS fix outside the accuracy policy',
  gps_no_fix: 'The device got no GPS fix',
  gps_permission_denied: 'Location permission was refused on the device',
  gps_timeout: 'The GPS fix timed out',
  photos_missing: 'A required photograph was missing',
};

/**
 * One deployment job in full, and the dispatcher's acts on it.
 *
 * An OPEN job (assigned / in progress) can be reassigned to another person
 * whose role can work a deployment job or cancelled; an INCOMPLETE one is
 * confirmed and closed with a reason by an administrator who was not the person
 * that submitted it (the server enforces that). These are quick record edits, not chassis writes, so they close on
 * success rather than moving to the background tray.
 */
export function FieldJobDetailDialog({
  job,
  engineers,
  canManage,
  canClose,
  onClose,
  onChanged,
}: {
  job: FieldJob;
  engineers: FieldEngineer[];
  canManage: boolean;
  canClose: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [reassignTo, setReassignTo] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const done = () => {
    onChanged();
    onClose();
  };
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : 'That did not go through');

  const reassign = useMutation({
    mutationFn: () => api.patch<FieldJob>(`/field/jobs/${encodeURIComponent(job.id)}`, { assignedTo: reassignTo }),
    onSuccess: done,
    onError: fail,
  });
  const cancel = useMutation({
    mutationFn: () => api.patch<FieldJob>(`/field/jobs/${encodeURIComponent(job.id)}`, { status: 'cancelled' }),
    onSuccess: done,
    onError: fail,
  });
  const close = useMutation({
    mutationFn: () => api.post<FieldJob>(`/field/jobs/${encodeURIComponent(job.id)}/close`, { reason: reason.trim() }),
    onSuccess: done,
    onError: fail,
  });

  const open = job.status === 'assigned' || job.status === 'in_progress';
  const busy = reassign.isPending || cancel.isPending || close.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-ink">{job.title}</h2>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted">
              <MapPin className="size-3" />
              {job.targetLabel}
            </p>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 text-muted hover:text-ink" aria-label="Close">
            <X className="size-4" />
          </button>
        </header>

        <div className="max-h-[65vh] space-y-4 overflow-y-auto p-5">
          {error && <ErrorNotice error={error} />}

          <div className="flex flex-wrap items-center gap-2">
            <Pill state tone={STATUS_TONE[job.status]}>{STATUS_LABEL[job.status]}</Pill>
            {job.assignedToName && <Pill tone="neutral">{job.assignedToName}</Pill>}
            <Pill state tone={job.requirements.gps === 'met' ? 'success' : job.requirements.gps === 'refused' ? 'warn' : 'neutral'}>
              GPS {job.requirements.gps}
            </Pill>
            {job.requirements.photos !== 'not_required' && job.requirements.photos !== 'disabled' && (
              <Pill state tone={job.requirements.photos === 'met' ? 'success' : 'warn'}>
                Photo {job.requirements.photos}
              </Pill>
            )}
            {/* The checklist (2026-09-16): named, not enforced — a missing required step sits beside the outcome. */}
            {job.requirements.checklist && job.requirements.checklist !== 'not_required' && (
              <Pill state
                tone={job.requirements.checklist === 'met' ? 'success' : 'warn'}
                title={
                  job.requirements.checklist === 'missing'
                    ? `Required steps left undone: ${(job.requirements.checklistMissing ?? []).join(', ')}`
                    : undefined
                }
              >
                Checklist {job.requirements.checklist}
              </Pill>
            )}
            {typeof job.lightReadingDbm === 'number' && (
              <Pill state tone={job.lightReadingDbm >= -27 && job.lightReadingDbm <= -8 ? 'success' : 'warn'} title="The light reading the technician recorded at the ONU">
                {formatChecklistValue({ kind: 'light_reading', done: true, value: job.lightReadingDbm })} at the ONU
              </Pill>
            )}
          </div>

          {job.incompleteReason && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
              {INCOMPLETE_LABEL[job.incompleteReason] ?? job.incompleteReason}
              {job.accuracyThresholdM !== null && job.incompleteReason === 'gps_accuracy' && (
                <> — the policy at submission was {job.accuracyThresholdM} m.</>
              )}
            </div>
          )}

          {job.checklist && job.checklist.length > 0 && (
            <div>
              <p className="mb-1 text-xs uppercase tracking-wide text-faint">Checklist</p>
              <ul className="divide-y divide-line rounded-lg border border-line">
                {job.checklist.map((step) => {
                  const value = formatChecklistValue(step);
                  return (
                    <li key={step.key} className="flex items-start gap-2 px-3 py-2 text-sm">
                      {step.done ? (
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                      ) : (
                        <Circle className="mt-0.5 size-4 shrink-0 text-faint" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={step.done ? 'text-ink' : 'text-muted'}>{step.label}</span>
                          {step.required && !step.done && <Pill tone="warn">Required</Pill>}
                          {value && <span className="font-mono text-xs text-ink">{value}</span>}
                        </div>
                        {step.done && (
                          <p className="text-xs text-faint">
                            {step.byName ?? 'Somebody'}
                            {step.at !== null && ` · ${formatRelative(step.at)}`}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {job.captures.length > 0 && (
            <div>
              <p className="mb-1 text-xs uppercase tracking-wide text-faint">Captures</p>
              <ul className="space-y-1.5">
                {job.captures.map((c) => (
                  <li key={c.id} className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-ink">{c.outcome.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-faint">{formatRelative(c.receivedAt)}</span>
                    </div>
                    {c.lat !== null && c.lng !== null ? (
                      <p className="mt-0.5 font-mono text-xs text-muted">
                        {c.lat.toFixed(6)}, {c.lng.toFixed(6)}
                        {c.accuracyM !== null && ` · ±${Math.round(c.accuracyM)} m`} · threshold {c.thresholdM} m
                      </p>
                    ) : (
                      <p className="mt-0.5 text-xs text-muted">No coordinate — {c.outcome.replace(/_/g, ' ')}.</p>
                    )}
                    {c.note && <p className="mt-0.5 text-xs text-muted">{c.note}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {job.photos.length > 0 && (
            <div>
              <p className="mb-1 text-xs uppercase tracking-wide text-faint">Photographs</p>
              <div className="flex flex-wrap gap-2">
                {job.photos.map((p) => (
                  <a
                    key={p.id}
                    href={`/api/field/jobs/${encodeURIComponent(job.id)}/photos/${encodeURIComponent(p.id)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                  >
                    <img
                      src={`/api/field/jobs/${encodeURIComponent(job.id)}/photos/${encodeURIComponent(p.id)}`}
                      alt={`${p.kind} photo`}
                      className="size-20 rounded-lg border border-line object-cover"
                    />
                    <span className="mt-0.5 block text-center text-[11px] text-faint">{p.kind}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {job.closeReason && (
            <div>
              <p className="mb-1 text-xs uppercase tracking-wide text-faint">Closed</p>
              <p className="text-sm text-muted">
                {job.closedByName ? `${job.closedByName}: ` : ''}
                {job.closeReason}
              </p>
            </div>
          )}

          {/* --- Dispatcher acts --- */}
          {canManage && open && (
            <div className="space-y-3 border-t border-line pt-4">
              <Field
                label="Reassign to somebody else"
                hint="Anyone whose role can work a deployment job."
              >
                <div className="flex gap-2">
                  <select
                    className="input"
                    value={reassignTo}
                    onChange={(e) => setReassignTo(e.target.value)}
                    disabled={busy}
                  >
                    <option value="">Choose a person…</option>
                    {engineers
                      .filter((e) => e.id !== job.assignedTo)
                      .map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name} — {e.email}
                        </option>
                      ))}
                  </select>
                  <Button
                    disabled={!reassignTo || busy}
                    loading={reassign.isPending}
                    onClick={() => reassign.mutate()}
                  >
                    Reassign
                  </Button>
                </div>
                <p className="mt-1 text-xs text-faint">
                  Reassigning resets the job to “assigned” — a fresh start for whoever picks it up.
                </p>
              </Field>
              <Button variant="danger" disabled={busy} loading={cancel.isPending} onClick={() => cancel.mutate()}>
                Cancel job
              </Button>
            </div>
          )}

          {job.status === 'incomplete' && (
            <div className="space-y-2 border-t border-line pt-4">
              {canClose ? (
                <>
                  <Field
                    label="Confirm and close"
                    hint="What was actually the case on site — the record that ends a deployment the field could not finish. At least 10 characters."
                  >
                    <textarea
                      className="input min-h-20"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      maxLength={2000}
                      placeholder="FAT is inside a locked yard; fix taken at the gate, verified against the as-built."
                    />
                  </Field>
                  <Button
                    variant="primary"
                    disabled={reason.trim().length < 10 || busy}
                    loading={close.isPending}
                    onClick={() => close.mutate()}
                  >
                    Close job
                  </Button>
                </>
              ) : (
                <p className="text-sm text-muted">
                  This job came back incomplete. An administrator with “Confirm and close deployment
                  jobs” signs it off — and not the person who submitted it.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
